import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getStudentDocuments, uploadStudentDocument, deleteStudentDocument } from './api/studentScreensApi'

export default function StudentDocumentScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')
  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [fileName, setFileName] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [docs, setDocs] = useState([])

  useEffect(() => { loadStudent(); loadDocs() }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadDocs = async () => {
    try {
      const rows = await getStudentDocuments(studentId)
      setDocs(rows || [])
    } catch {
      setDocs([])
    }
  }

  const goBack = () => navigate('/admission/students/list')
  const name = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    setSelectedFile(file || null)
    setFileName(file?.name || '')
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    setUploading(true)
    try {
      await uploadStudentDocument(studentId, selectedFile)
      setSelectedFile(null)
      setFileName('')
      document.getElementById('student-doc-upload').value = ''
      await loadDocs()
    } catch {
      // ignore
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (docId) => {
    try {
      await deleteStudentDocument(studentId, docId)
      await loadDocs()
    } catch {
      // ignore
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Document</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">Document — {name}</h1>
        </div>
        <button type="button" onClick={goBack} className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface" title="Back">
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
        </button>
      </section>

      <div className="flex items-center gap-4 rounded-2xl border border-outline-variant/30 bg-surface-container-low p-4">
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
          onChange={handleFileChange}
          className="hidden"
          id="student-doc-upload"
        />
        <label htmlFor="student-doc-upload" className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-surface-container px-4 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition-colors">
          <span className="material-symbols-outlined text-[16px]">upload_file</span>
          Choose File
        </label>
        <span className="text-xs text-on-surface-variant">{fileName || 'No file chosen'}</span>
        <button type="button" onClick={handleUpload} disabled={!selectedFile || uploading} className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-on-primary shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
          {uploading ? (
            <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
          ) : (
            <span className="material-symbols-outlined text-[14px]">cloud_upload</span>
          )}
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
        <span className="text-xs text-on-surface-variant/70">(JPG, PNG, GIF, WEBP or PDF supported, max 10MB)</span>
      </div>

      <div className="flex items-center gap-3 rounded-2xl bg-primary px-6 py-4">
        <span className="material-symbols-outlined text-on-primary text-[22px]">description</span>
        <h2 className="text-base font-bold text-on-primary">Upload Document</h2>
      </div>

      <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center px-6 py-16">
            <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
            <p className="mt-3 text-sm text-on-surface-variant">Loading documents...</p>
          </div>
        ) : docs.length === 0 ? (
          <div className="px-5 py-12">
            <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
              <p className="text-center text-sm font-bold leading-relaxed text-blue-600">
                No documents uploaded yet. Use the file picker above to upload files.
              </p>
            </div>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/30 bg-surface-container-low">
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[80px]">Action</th>
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">File Name</th>
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[200px]">Uploaded On</th>
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[120px]">Size</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc, idx) => (
                <tr key={doc.id} className={`border-b border-outline-variant/10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1">
                      <a
                        href={doc.url || `#`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-on-surface-variant hover:bg-primary/10 hover:text-primary transition-colors"
                        title="View"
                      >
                        <span className="material-symbols-outlined text-[16px]">visibility</span>
                      </a>
                      <button type="button" onClick={() => handleDelete(doc.id)} className="flex h-7 w-7 items-center justify-center rounded-lg text-on-surface-variant hover:bg-error/10 hover:text-error transition-colors" title="Delete">
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-[18px] text-primary/70">image</span>
                      <span className="text-sm font-semibold text-on-surface">{doc.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-on-surface-variant">{doc.uploadedOn}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface-variant font-mono">{doc.size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on <span className="font-semibold text-on-surface">{student.createdDate}</span></span>
          )}
          {student?.updatedBy && (
            <span>Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on <span className="font-semibold text-on-surface">{student.updatedDate}</span></span>
          )}
        </div>
      </section>
    </div>
  )
}
